import { useApolloClient } from '@apollo/client'
import update from 'immutability-helper'

import {
  MUTATION_REPLY,
  MUTATION_DISMISS_COMMENT,
  MUTATION_UNDISMISS_COMMENT,
} from '~/modules/post/queries'

import { usePost } from '~/modules/post/hooks'
import { COMMENT_STATUS } from '~/modules/post/constants'
import { useTrackCommentProcessed } from '~/shared/hooks/tracking'
import { useEngageAccount } from '~/modules/account/hooks'
import { useCurrentChannel, useCurrentPost } from '~/app/state/hooks'
import { ERROR_CHANNEL_DISCONNECTED } from '~/app/constants'
import { ENGAGE_ACTIONS } from '@buffer-mono/app-shell/src/common/events/engageEvents'

const CODE_OPTIMISTIC_RESPONSE = 'OPTIMISTIC_RESPONSE'

/**
 *
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'postId' implicitly has an 'any' type.
export default function useCommentProcessing(postId) {
  const client = useApolloClient()

  const trackCommentProcessed = useTrackCommentProcessed()
  const { getPost, setPost } = usePost()
  const { getCurrentChannel } = useCurrentChannel()
  const { mutateCurrentPost } = useCurrentPost()
  const { getEngageAccount, setEngageAccount } = useEngageAccount()

  /**
   *
   */
  // @ts-expect-error TS(7031) FIXME: Binding element 'comment' implicitly has an 'any' ... Remove this comment to see the full error message
  const processComment = ({ comment, text, tracking = {} }) => {
    const textEmpty = text !== undefined && !text
    if (textEmpty) {
      return
    }

    const mutationName = text ? 'engageReply' : 'engageDismiss'

    mutateComment({
      operation: 'process',
      mutationName,
      mutationValues: { comment, text, tracking },
    })
  }

  /**
   *
   */
  // @ts-expect-error TS(7031) FIXME: Binding element 'comment' implicitly has an 'any' ... Remove this comment to see the full error message
  const unprocessComment = ({ comment, tracking = {} }) => {
    mutateComment({
      operation: 'unprocess',
      mutationName: 'engageUnDismiss',
      mutationValues: { comment, tracking },
    })
  }

  /**
   *
   */
  // @ts-expect-error TS(7031) FIXME: Binding element 'operation' implicitly has an 'any... Remove this comment to see the full error message
  const mutateComment = ({ operation, mutationName, mutationValues }) => {
    const { comment, text, tracking } = mutationValues

    if (!comment) {
      return
    }

    const currentChannel = getCurrentChannel()
    const post = getPost(postId)

    client.mutate({
      // @ts-expect-error TS(2322) FIXME: Type 'DocumentNode | undefined' is not assignable ... Remove this comment to see the full error message
      mutation: getMutationQuery(mutationName),
      variables: {
        postId,
        commentId: comment.id,
        text,
      },
      optimisticResponse: buildOptimisticResponse({
        currentChannel,
        post,
        comment,
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ currentChannel: any; post: any... Remove this comment to see the full error message
        text,
        mutationName,
      }),
      update: (_, { data }) => {
        if (!data) {
          console.error('useCommentProcessing(): No data returned on update.')
          return false
        }

        const res = data[mutationName]
        const isOptimistic = res.code === CODE_OPTIMISTIC_RESPONSE

        // Load current version of the post.
        const currPost = getPost(postId)
        if (!currPost) {
          console.error('useCommentProcessing(): Post not found: ', postId)
          return false
        }

        const postUpdateCmd = {}
        const channelUpdateCmd = {}

        // Only update counters if data is newer.
        // NOTE: If the 'id' of both the channel and the post are included on the
        // mutation response schema, Apollo will take care of updating the counters
        // automatically. We are seeing a race condition were counters are left
        // on a wrong state with old values and we are not yet sure why that's happening
        // so we are adding this here for precaution to discard older responses if any.
        const needsUpdate =
          new Date(res.post.commentsUpdatedAt) >
          new Date(currPost.commentsUpdatedAt)
        if (needsUpdate && !isOptimistic) {
          // @ts-expect-error TS(2339) FIXME: Property 'totalPendingComments' does not exist on ... Remove this comment to see the full error message
          postUpdateCmd.totalPendingComments = {
            $set: res.post.totalPendingComments,
          }
          // @ts-expect-error TS(2339) FIXME: Property 'labelsCount' does not exist on type '{}'... Remove this comment to see the full error message
          postUpdateCmd.labelsCount = { $set: res.post.labelsCount }
          // @ts-expect-error TS(2339) FIXME: Property 'commentsUpdatedAt' does not exist on typ... Remove this comment to see the full error message
          postUpdateCmd.commentsUpdatedAt = { $set: res.post.commentsUpdatedAt }

          // @ts-expect-error TS(2339) FIXME: Property 'totalPendingComments' does not exist on ... Remove this comment to see the full error message
          channelUpdateCmd.totalPendingComments = {
            $set: res.channel.totalPendingComments,
          }
          // @ts-expect-error TS(2339) FIXME: Property 'labelsCount' does not exist on type '{}'... Remove this comment to see the full error message
          channelUpdateCmd.labelsCount = { $set: res.channel.labelsCount }
        }

        if (res.replySent) {
          const commentIndex = currPost.comments.findIndex(
            // @ts-expect-error TS(7006) FIXME: Parameter 'el' implicitly has an 'any' type.
            (el) => el.id === res.comment.id,
          )
          // @ts-expect-error TS(2339) FIXME: Property 'comments' does not exist on type '{}'.
          postUpdateCmd.comments = {
            [commentIndex]: {
              replies: {
                $push: [res.replySent],
              },
            },
          }
        }

        // Mark channel as disconnected if needed.
        if (res.code === ERROR_CHANNEL_DISCONNECTED) {
          // @ts-expect-error TS(2339) FIXME: Property 'isDisconnected' does not exist on type '... Remove this comment to see the full error message
          channelUpdateCmd.isDisconnected = { $set: true }
        }

        // Update post.
        const updatedPost = update(currPost, postUpdateCmd)
        setPost(postId, updatedPost)
        mutateCurrentPost(updatedPost)

        // Update Engage organization.
        const orgId = currentChannel.organizationId
        const engageAccount = getEngageAccount(orgId)
        const currChannelIndex = engageAccount.channels.findIndex(
          // @ts-expect-error TS(7006) FIXME: Parameter 'el' implicitly has an 'any' type.
          (el) => el.id === currentChannel.serviceId,
        )
        const updatedEngageAccount = update(engageAccount, {
          channels: { [currChannelIndex]: channelUpdateCmd },
        })
        setEngageAccount(orgId, updatedEngageAccount)

        // Send tracking event if needed. We don't track un-dismissing
        // comments for now.
        if (!isOptimistic && res.success && operation === 'process') {
          trackCommentProcessed(mutationName, updatedPost, comment, tracking)
        }

        // RefetchEngageComments
        ENGAGE_ACTIONS.refetchEngageComments()
      },
    })
  }

  return {
    processComment,
    unprocessComment,
  }
}

/**
 *
 */
function buildOptimisticResponse({
  // @ts-expect-error TS(7031) FIXME: Binding element 'currentChannel' implicitly has an... Remove this comment to see the full error message
  currentChannel,
  // @ts-expect-error TS(7031) FIXME: Binding element 'post' implicitly has an 'any' typ... Remove this comment to see the full error message
  post,
  // @ts-expect-error TS(7031) FIXME: Binding element 'comment' implicitly has an 'any' ... Remove this comment to see the full error message
  comment,
  // @ts-expect-error TS(7031) FIXME: Binding element 'mutationName' implicitly has an '... Remove this comment to see the full error message
  mutationName,
}) {
  // NOTE: We could avoid doing this if at all times, when comments
  // are selected, we keep track of what the parent comment is.
  const parentComment = getParentComment(post, comment)
  const isParentComment = parentComment.id === comment.id

  return {
    __typename: 'Mutation',
    [mutationName]: {
      __typename: 'EngagementMutationResponse',
      success: true,
      code: 'OPTIMISTIC_RESPONSE',
      message: '',
      comment: {
        __typename: 'Comment',
        id: parentComment.id,
        needsAttention: isParentComment
          ? !parentComment.needsAttention
          : parentComment.needsAttention,
        status: isParentComment
          ? getProcessingStatus(mutationName)
          : parentComment.status,
        error: null,
      },
      reply: !isParentComment
        ? {
            __typename: 'Reply',
            id: comment.id,
            needsAttention: !comment.needsAttention,
            status: getProcessingStatus(mutationName),
            error: null,
          }
        : null,
      post: {
        __typename: 'Post',
        totalPendingComments: post.totalPendingComments,
        labelsCount: [...post.labelsCount],
        commentsUpdatedAt: post.commentsUpdatedAt,
      },
      channel: {
        __typename: 'EngageChannel',
        labelsCount: currentChannel.labelsCount,
        totalPendingComments: currentChannel.totalPendingComments,
      },
      replySent: null,
    },
  }
}

/**
 *
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'post' implicitly has an 'any' type.
function getParentComment(post, comment) {
  if (!post.comments || !comment) {
    return null
  }

  let parent = null

  for (const postComment of post.comments) {
    parent = postComment

    // @ts-expect-error TS(7006) FIXME: Parameter 'el' implicitly has an 'any' type.
    const reply = postComment.replies?.find((el) => el.id === comment.id)

    if (postComment.id === comment.id || reply) {
      break
    }
  }

  return parent
}

/**
 *
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'mutationName' implicitly has an 'any' t... Remove this comment to see the full error message
function getProcessingStatus(mutationName) {
  switch (mutationName) {
    case 'engageReply':
      return COMMENT_STATUS.SENDING
    case 'engageDismiss':
      return COMMENT_STATUS.DISMISSING
    case 'engageUnDismiss':
      return COMMENT_STATUS.OPENING
  }
}

/**
 *
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'mutationName' implicitly has an 'any' t... Remove this comment to see the full error message
function getMutationQuery(mutationName) {
  switch (mutationName) {
    case 'engageReply':
      return MUTATION_REPLY
    case 'engageDismiss':
      return MUTATION_DISMISS_COMMENT
    case 'engageUnDismiss':
      return MUTATION_UNDISMISS_COMMENT
  }
}
